import { useState } from 'react'

import { CustomFieldType } from '@tribeplatform/gql-client/types'
import { CustomFieldSubtype } from '@tribeplatform/react-components/CustomField'
import type {
  Filter,
  InlineFilter,
} from '@tribeplatform/react-components/Filters'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { GenericInlineFilterItemSettings } from '@tribeplatform/react-components/Views'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { LoadingOverlay } from '@tribeplatform/react-ui-kit/LoadingOverlay'
import { Toggle } from '@tribeplatform/react-ui-kit/Toggle'

import { NarrowDownValuePicker } from '../components/NarrowDownValuePicker.js'
import type { SetInlineFilterProperty } from '../types.js'

interface Props {
  inlineFilter: InlineFilter
  processedInlineFilter?: Filter
  setProperty: SetInlineFilterProperty
  goBack: () => void
}
export const InlineFilterItemSettings = ({
  inlineFilter,
  processedInlineFilter,
  setProperty,
  goBack,
}: Props) => {
  const { $t } = useI18n()
  const [labelError, setLabelError] = useState(false)
  const [showValuePicker, setShowValuePicker] = useState(
    processedInlineFilter?.extraProps?.narrowedDownOptions?.length > 0,
  )
  const isArray =
    processedInlineFilter?.type === CustomFieldType.relation ||
    processedInlineFilter?.type === CustomFieldType.array ||
    processedInlineFilter?.subType === CustomFieldSubtype.SELECT ||
    processedInlineFilter?.subType === CustomFieldSubtype.MULTISELECT
  const inlineSelectOptions =
    processedInlineFilter?.extraProps?.inlineSelectOptions ?? []
  const selectOptions = processedInlineFilter?.extraProps?.selectOptions ?? []
  const canNarrowDown =
    isArray &&
    (inlineSelectOptions.length === 0 || inlineSelectOptions.length > 1) &&
    (selectOptions.length === 0 || selectOptions.length > 1)

  return (
    <GenericInlineFilterItemSettings
      goBack={goBack}
      title={inlineFilter.label}
      enabled={inlineFilter.enabled}
      setEnabled={enabled => setProperty('enabled', enabled)}
    >
      <LoadingOverlay isLoading={!inlineFilter.enabled} pulse={false}>
        <div className="space-y-5">
          <FormControl.Input
            name="customLabel"
            label={<T defaultMessage="Label" id="Generics.Label.Noun" />}
            value={labelError ? '' : inlineFilter.customLabel}
            error={$t({
              defaultMessage: 'Please provide a label for this inline filter',
              id: 'InlineFilterSettings.Validation.LabelRequired',
            })}
            invalid={labelError}
            onChange={e => {
              const { value } = e.target

              if (!value || !value.trim()) {
                setLabelError(true)
              } else {
                setLabelError(false)
                setProperty('customLabel', value)
              }
            }}
          />
          <Toggle
            title={
              <T
                defaultMessage="Enable operators"
                id="InlineFilterSettings.EnableOperators"
              />
            }
            description={
              <T
                defaultMessage="Enable users with operators to refine filters"
                id="InlineFilterSettings.EnableOperatorsDescription"
              />
            }
            checked={!!inlineFilter.allowOperatorSelection}
            onChange={value => {
              setProperty('allowOperatorSelection', !!value)
            }}
          />
          {canNarrowDown && (
            <Toggle
              title={
                <T
                  defaultMessage="Handpick filters"
                  id="InlineFilterSettings.HandPickFilters"
                />
              }
              description={
                <T
                  defaultMessage="Select only filters you want to be shown"
                  id="InlineFilterSettings.HandPickFiltersDescription"
                />
              }
              checked={showValuePicker}
              onChange={value => {
                setShowValuePicker(!!value)
                if (!value) {
                  setProperty('narrowedDownOptions', [])
                }
              }}
            />
          )}
          {showValuePicker && (
            <div className="space-y-2">
              <NarrowDownValuePicker
                processedInlineFilter={processedInlineFilter}
                onChange={options => {
                  setProperty('narrowedDownOptions', options)
                }}
              />
            </div>
          )}
        </div>
      </LoadingOverlay>
    </GenericInlineFilterItemSettings>
  )
}
